/**
 * PrCardGenerator — portal modal that lets a user export/share/post a PR card.
 *
 * Usage:
 *   <PrCardGenerator open={open} onClose={close} pr={pr} userName={user?.name} />
 *
 * pr shape: { exerciseName, weight, reps, prType, prevBest }
 *   (prType === 'reps' or weight <= 0 → bodyweight rep PR, rendered as "{reps} REPS")
 *
 * Mirrors MilestoneCardGenerator. html2canvas captured at export resolution,
 * three actions: Post to Feed (primary), Share (external), Download.
 */

import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Share2, RectangleVertical, Send, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import client from '../api/client';
import { useNativeShare } from '../hooks/useNativeShare';
import { templateFor } from './PrCardTemplates';
import { getDisplayName } from '../lib/displayName';

const FONT = "'Barlow Condensed', sans-serif";

const RATIOS = {
  story: { w: 1080, h: 1920, label: '9:16', previewW: 220 },
  square: { w: 1080, h: 1080, label: '1:1', previewW: 260 },
};

export default function PrCardGenerator({ open, onClose, pr, userName = '' }) {
  const displayUserName = getDisplayName(userName);
  const { t } = useTranslation();
  const [ratio, setRatio] = useState('story');
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [posting, setPosting] = useState(false);
  const slideRef = useRef(null);
  const { shareImage, downloadImage } = useNativeShare();

  if (!open || !pr) return null;

  const r = RATIOS[ratio];
  const Template = templateFor(ratio);
  const previewH = r.previewW * (r.h / r.w);
  const previewScale = r.previewW / r.w;

  const captureCanvas = async () => {
    if (!slideRef.current) throw new Error('No ref');
    const { default: html2canvas } = await import('html2canvas');
    return html2canvas(slideRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      width: r.w,
      height: r.h,
    });
  };

  const isRepsPr = pr.prType === 'reps' || (Number(pr.weight) <= 0 && Number(pr.reps) > 0);
  const prSlug = isRepsPr ? `${pr.reps}reps` : `${pr.weight}kg`;
  const safeFileName = `trainedbyrs-pr-${(pr.exerciseName || 'lift').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${prSlug}.png`;
  const shareText = isRepsPr
    ? `New rep PR! ${pr.reps} reps on ${pr.exerciseName} 🏆🔥`
    : `New all-time PR! ${pr.weight} KG on ${pr.exerciseName} 🏆🔥`;

  const handleExport = async () => {
    setExporting(true);
    try {
      const canvas = await captureCanvas();
      downloadImage(canvas, safeFileName);
      toast.success(t('pr.cardDownloaded', 'Card saved!'));
    } catch {
      toast.error(t('pr.cardExportFailed', 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const canvas = await captureCanvas();
      const result = await shareImage(canvas, {
        title: 'trainedbyRS — New PR!',
        text: shareText,
        fileName: safeFileName,
        successMessage: t('pr.cardDownloaded', 'Card downloaded — share it!'),
        errorMessage: t('pr.cardShareFailed', 'Failed to share'),
      });
      if (result === 'shared') {
        toast.success(t('social.storyShared', 'Shared successfully!'));
      }
    } catch {
      toast.error(t('pr.cardShareFailed', 'Failed to share'));
    } finally {
      setSharing(false);
    }
  };

  const handlePostToFeed = async () => {
    setPosting(true);
    try {
      const canvas = await captureCanvas();
      const imageData = canvas.toDataURL('image/png');
      await client.post('/social/posts/pr', {
        exercise_name: pr.exerciseName,
        weight_kg: pr.weight,
        reps: pr.reps || 1,
        pr_type: pr.prType || 'weight',
        prev_best_kg: pr.prevBest || null,
        image: imageData,
      });
      toast.success(t('pr.postedToFeed', 'Posted to your feed!'));
      onClose();
    } catch {
      toast.error(t('pr.postToFeedFailed', 'Failed to post to feed'));
    } finally {
      setPosting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/90 flex flex-col items-center justify-start overflow-y-auto p-4 pb-8"
      data-testid="pr-card-generator"
    >
      <div className="w-full max-w-xs">
        <div className="flex items-center justify-between mb-4">
          <h2
            className="text-white font-bold text-xl uppercase tracking-wide"
            style={{ fontFamily: FONT }}
          >
            {t('pr.shareCard', 'Share PR')}
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1" data-testid="pr-card-close">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex gap-2 mb-5">
          {Object.entries(RATIOS).map(([key, val]) => (
            <button
              key={key}
              onClick={() => setRatio(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                ratio === key ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:text-white'
              }`}
            >
              {key === 'story' ? <RectangleVertical className="h-3 w-3" /> : <Square className="h-3 w-3" />}
              {val.label}
            </button>
          ))}
        </div>

        {/* Canvas preview — template renders at full export size, CSS-scaled for display */}
        <div className="flex justify-center mb-5">
          <div
            style={{
              width: r.previewW,
              height: previewH,
              overflow: 'hidden',
              flexShrink: 0,
              borderRadius: 12,
              position: 'relative',
            }}
          >
            <div
              style={{
                transform: `scale(${previewScale})`,
                transformOrigin: 'top left',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <div
                ref={slideRef}
                style={{ width: r.w, height: r.h }}
              >
                <Template pr={pr} userName={displayUserName} previewW={r.w} previewH={r.h} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={handlePostToFeed}
            disabled={sharing || exporting || posting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold disabled:opacity-50 transition-colors"
            data-testid="pr-card-post-feed"
          >
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {t('pr.postToFeed', 'Post to Feed')}
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleShare}
              disabled={sharing || exporting || posting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold disabled:opacity-50 transition-colors"
              data-testid="pr-card-share"
            >
              {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              {t('social.share', 'Share')}
            </button>
            <button
              onClick={handleExport}
              disabled={sharing || exporting || posting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold disabled:opacity-50 transition-colors"
              data-testid="pr-card-download"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t('social.download', 'Download')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
